import { useTranslation } from 'next-i18next';

import { logger } from '@zep/core-logger';

import { useAlert } from '@/hooks';
import { useBuildFormContext } from '@/views/build/form/schema';

import { focusField } from '../components/focus/focusUtils';
import { CUSTOM_PUBLISHER_REQUIRED_MESSAGE } from '../form/schema/BuildPublisherInfoSchema';
import {
  AT_LEAST_TWO_CHARACTER_REQUIRED_MESSAGE,
  QUIZ_MAP_TEMPLATE_REQUIRED_MESSAGE,
  SUBJECT_REQUIRED_MESSAGE,
} from '../form/schema/BuildQuizSetBasicInfoSchema';
import {
  GRADE_REQUIRED_MESSAGE,
  SCHOOL_TYPE_REQUIRED_MESSAGE,
  SEMESTER_REQUIRED_MESSAGE,
} from '../form/schema/BuildSchoolInfoSchema';

import { useBuildSchema } from './useBuildSchema';

export const useBuildValidate = () => {
  const { t } = useTranslation(['build']);
  const dynamicBuildSchema = useBuildSchema();

  const { getValues, setError } = useBuildFormContext();
  const alertModal = useAlert();

  const validateAll = () => {
    const formData = getValues();

    let confirmAction: () => void;
    let errorMessage: string | undefined = undefined;

    const { error, success } = dynamicBuildSchema.safeParse(formData);

    if (!success) {
      const { errors: issues } = error;
      const errorMessages = issues.map(issue => issue.message);
      let failureReason: string | undefined;

      //위계에 따른 유효성 검사
      if (errorMessages.includes(AT_LEAST_TWO_CHARACTER_REQUIRED_MESSAGE)) {
        errorMessage = t('input.quizTitle.minLength', { n: 2 });
        confirmAction = () => {
          focusField('title');
        };
        failureReason = 'no_title';
      } else if (errorMessages.includes(QUIZ_MAP_TEMPLATE_REQUIRED_MESSAGE)) {
        errorMessage = t('build.alert.noBackground');
        confirmAction = () => {
          focusField('templateId');
        };
        failureReason = 'no_map_template';
      } else if (
        errorMessages.includes(SCHOOL_TYPE_REQUIRED_MESSAGE) ||
        errorMessages.includes(GRADE_REQUIRED_MESSAGE) ||
        errorMessages.includes(SEMESTER_REQUIRED_MESSAGE)
      ) {
        errorMessage = t('schoolInfo.required.all');
        confirmAction = () => {
          focusField('schoolTypeId');
          setError('schoolTypeId', {
            type: 'min',
          });
        };
        failureReason = 'missing_school_info';
      } else if (errorMessages.includes(SUBJECT_REQUIRED_MESSAGE)) {
        errorMessage = t('build:build.alert.noSubject');
        confirmAction = () => {
          focusField('subject');
          setError('subject', {
            type: 'min',
          });
        };
        failureReason = 'no_subject';
      } else if (errorMessages.includes(CUSTOM_PUBLISHER_REQUIRED_MESSAGE)) {
        errorMessage = t('input.publisher.minLength', { n: 2 });
        confirmAction = () => {
          setError('customPublisher', {
            type: 'min',
          });
          focusField('customPublisher');
        };
        failureReason = 'invalid_custom_publisher';
      }

      if (failureReason) {
        logger.track('quiz_build_validation_failed', {
          failure_reason: failureReason,
        });
      }
    }

    if (errorMessage) {
      void alertModal(
        {
          type: 'alert',
          title: errorMessage,
        },
        () => {
          confirmAction?.();
        },
      );
      return false;
    }
    return true;
  };

  return {
    validateAll,
  };
};
